// src/services/DiarySupabaseService.ts
/**
 * 📓 Diary Supabase Service
 * Gestisce CRUD operations per diary pages, trade entries e reports
 */

import { supabase } from '../config/supabase';

// =====================================================
// TYPES
// =====================================================

export interface DiaryBlock {
  id: string;
  type: 'p' | 'h1' | 'h2' | 'h3' | 'trade' | 'page' | 'divider' | 'ul' | 'ol';
  content: string;
  metadata?: any;
}

export interface DiaryPage {
  id: string;
  userId?: string;
  title: string;
  type: 'page' | 'subpage' | 'folder';
  parentId?: string;
  blocks: DiaryBlock[];
  tags: string[];
  order: number;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DiaryTradeEntry {
  id: string;
  userId?: string;
  pageId: string;
  blockId: string;
  title: string;
  datetime: string;
  entryPrice?: number;
  exitPrice?: number;
  strategyId?: string;
  preTradeConfidence?: number;
  postTradeConfidence?: number;
  sentiment?: 'bullish' | 'bearish' | 'neutral';
  screenshot?: string;
  notes?: string;
  status: 'open' | 'closed';
  createdAt?: string;
  updatedAt?: string;
}

export interface SavedReport {
  id: string;
  userId?: string;
  name: string;
  description?: string;
  config: any;
  data?: any;
  createdAt?: string;
  updatedAt?: string;
  lastGeneratedAt?: string;
}

// =====================================================
// DIARY PAGES
// =====================================================

export class DiarySupabaseService {

  /**
   * Get all diary pages for current user
   */
  static async getUserPages(): Promise<DiaryPage[]> {
    try {
      const { data: pages, error } = await supabase
        .from('diary_pages')
        .select('*')
        .order('created_at', { ascending: false }); // 🔧 FIX: rimosso doppio order che causava perdita pagine

      if (error) {
        console.error('❌ Error fetching diary pages:', error);
        return [];
      }

      if (!pages || pages.length === 0) {
        console.log('📓 No diary pages found for user');
        return [];
      }

      // Transform from DB format to app format
      const diaryPages: DiaryPage[] = pages.map(page => ({
        id: page.id,
        userId: page.user_id,
        title: page.title,
        type: page.type as 'page' | 'subpage' | 'folder',
        parentId: page.parent_id,
        blocks: Array.isArray(page.blocks) ? page.blocks : [],
        tags: Array.isArray(page.tags) ? page.tags : [],
        order: page.order || 0,
        isArchived: page.is_archived || false,
        createdAt: page.created_at,
        updatedAt: page.updated_at
      }));

      console.log(`✅ Loaded ${diaryPages.length} diary pages`);
      return diaryPages;

    } catch (error: any) {
      console.error('❌ Error loading diary pages:', error);
      return [];
    }
  }

  /**
   * Create new diary page
   */
  static async createPage(page: Partial<DiaryPage>): Promise<{
    success: boolean;
    error?: string;
    page?: DiaryPage;
  }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      let user = session?.user ?? null;
      let userError: any = null;
      if (!user) {
        const _u = await supabase.auth.getUser();
        user = _u.data.user;
        userError = _u.error;
      }

      if (userError || !user) {
        return { success: false, error: 'Not authenticated' };
      }

      const { data, error } = await supabase
        .from('diary_pages')
        .insert({
          user_id: user.id,
          title: page.title || 'Untitled',
          type: page.type || 'page',
          parent_id: page.parentId || null,
          blocks: page.blocks || [],
          tags: page.tags || [],
          order: page.order || 0,
          is_archived: page.isArchived || false
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Page creation error:', error);
        return { success: false, error: error.message };
      }

      const createdPage: DiaryPage = {
        id: data.id,
        userId: data.user_id,
        title: data.title,
        type: data.type,
        parentId: data.parent_id,
        blocks: data.blocks || [],
        tags: data.tags || [],
        order: data.order,
        isArchived: data.is_archived,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };

      console.log(`✅ Created diary page: ${createdPage.title}`);
      return { success: true, page: createdPage };

    } catch (error: any) {
      console.error('❌ Page creation exception:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update existing diary page
   */
  static async updatePage(pageId: string, updates: Partial<DiaryPage>): Promise<{
    success: boolean;
    error?: string;
    page?: DiaryPage;
  }> {
    try {
      const updateData: any = {};

      if (updates.title !== undefined) updateData.title = updates.title;
      if (updates.type !== undefined) updateData.type = updates.type;
      if (updates.parentId !== undefined) updateData.parent_id = updates.parentId;
      if (updates.blocks !== undefined) updateData.blocks = updates.blocks;
      if (updates.tags !== undefined) updateData.tags = updates.tags;
      if (updates.order !== undefined) updateData.order = updates.order;
      if (updates.isArchived !== undefined) updateData.is_archived = updates.isArchived;

      const { data, error } = await supabase
        .from('diary_pages')
        .update(updateData)
        .eq('id', pageId)
        .select()
        .single();

      if (error) {
        console.error('❌ Page update error:', error);
        return { success: false, error: error.message };
      }

      const updatedPage: DiaryPage = {
        id: data.id,
        userId: data.user_id,
        title: data.title,
        type: data.type,
        parentId: data.parent_id,
        blocks: data.blocks || [],
        tags: data.tags || [],
        order: data.order,
        isArchived: data.is_archived,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };

      console.log(`✅ Updated diary page: ${pageId}`);
      return { success: true, page: updatedPage };

    } catch (error: any) {
      console.error('❌ Page update exception:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete diary page (and all its subpages cascade)
   */
  static async deletePage(pageId: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const { error } = await supabase
        .from('diary_pages')
        .delete()
        .eq('id', pageId);

      if (error) {
        console.error('❌ Delete error:', error);
        return { success: false, error: error.message };
      }

      console.log(`✅ Deleted diary page: ${pageId}`);
      return { success: true };

    } catch (error: any) {
      console.error('❌ Delete exception:', error);
      return { success: false, error: error.message };
    }
  }

  // =====================================================
  // DIARY TRADE ENTRIES
  // =====================================================

  /**
   * Get all trade entries for current user
   */
  static async getUserTradeEntries(pageId?: string): Promise<DiaryTradeEntry[]> {
    try {
      let query = supabase
        .from('diary_trade_entries')
        .select('*')
        .order('datetime', { ascending: false });

      if (pageId) {
        query = query.eq('page_id', pageId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('❌ Error fetching trade entries:', error);
        return [];
      }

      if (!data || data.length === 0) {
        return [];
      }

      const entries: DiaryTradeEntry[] = data.map(entry => ({
        id: entry.id,
        userId: entry.user_id,
        pageId: entry.page_id,
        blockId: entry.block_id,
        title: entry.title,
        datetime: entry.datetime,
        entryPrice: entry.entry_price,
        exitPrice: entry.exit_price,
        strategyId: entry.strategy_id,
        preTradeConfidence: entry.pre_trade_confidence,
        postTradeConfidence: entry.post_trade_confidence,
        sentiment: entry.sentiment,
        screenshot: entry.screenshot,
        notes: entry.notes,
        status: entry.status,
        createdAt: entry.created_at,
        updatedAt: entry.updated_at
      }));

      console.log(`✅ Loaded ${entries.length} trade entries`);
      return entries;

    } catch (error: any) {
      console.error('❌ Error loading trade entries:', error);
      return [];
    }
  }

  /**
   * Create new trade entry
   */
  static async createTradeEntry(entry: Partial<DiaryTradeEntry>): Promise<{
    success: boolean;
    error?: string;
    entry?: DiaryTradeEntry;
  }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      let user = session?.user ?? null;
      let userError: any = null;
      if (!user) {
        const _u = await supabase.auth.getUser();
        user = _u.data.user;
        userError = _u.error;
      }

      if (userError || !user) {
        return { success: false, error: 'Not authenticated' };
      }

      const { data, error } = await supabase
        .from('diary_trade_entries')
        .insert({
          user_id: user.id,
          page_id: entry.pageId,
          block_id: entry.blockId,
          title: entry.title,
          datetime: entry.datetime,
          entry_price: entry.entryPrice,
          exit_price: entry.exitPrice,
          strategy_id: entry.strategyId,
          pre_trade_confidence: entry.preTradeConfidence,
          post_trade_confidence: entry.postTradeConfidence,
          sentiment: entry.sentiment,
          screenshot: entry.screenshot,
          notes: entry.notes,
          status: entry.status || 'open'
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Trade entry creation error:', error);
        return { success: false, error: error.message };
      }

      const createdEntry: DiaryTradeEntry = {
        id: data.id,
        userId: data.user_id,
        pageId: data.page_id,
        blockId: data.block_id,
        title: data.title,
        datetime: data.datetime,
        entryPrice: data.entry_price,
        exitPrice: data.exit_price,
        strategyId: data.strategy_id,
        preTradeConfidence: data.pre_trade_confidence,
        postTradeConfidence: data.post_trade_confidence,
        sentiment: data.sentiment,
        screenshot: data.screenshot,
        notes: data.notes,
        status: data.status,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };

      console.log(`✅ Created trade entry: ${createdEntry.title}`);
      return { success: true, entry: createdEntry };

    } catch (error: any) {
      console.error('❌ Trade entry creation exception:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update trade entry
   */
  static async updateTradeEntry(entryId: string, updates: Partial<DiaryTradeEntry>): Promise<{
    success: boolean;
    error?: string;
    entry?: DiaryTradeEntry;
  }> {
    try {
      const updateData: any = {};

      if (updates.title !== undefined) updateData.title = updates.title;
      if (updates.datetime !== undefined) updateData.datetime = updates.datetime;
      if (updates.entryPrice !== undefined) updateData.entry_price = updates.entryPrice;
      if (updates.exitPrice !== undefined) updateData.exit_price = updates.exitPrice;
      if (updates.strategyId !== undefined) updateData.strategy_id = updates.strategyId;
      if (updates.preTradeConfidence !== undefined) updateData.pre_trade_confidence = updates.preTradeConfidence;
      if (updates.postTradeConfidence !== undefined) updateData.post_trade_confidence = updates.postTradeConfidence;
      if (updates.sentiment !== undefined) updateData.sentiment = updates.sentiment;
      if (updates.screenshot !== undefined) updateData.screenshot = updates.screenshot;
      if (updates.notes !== undefined) updateData.notes = updates.notes;
      if (updates.status !== undefined) updateData.status = updates.status;

      const { data, error } = await supabase
        .from('diary_trade_entries')
        .update(updateData)
        .eq('id', entryId)
        .select()
        .single();

      if (error) {
        console.error('❌ Trade entry update error:', error);
        return { success: false, error: error.message };
      }

      const updatedEntry: DiaryTradeEntry = {
        id: data.id,
        userId: data.user_id,
        pageId: data.page_id,
        blockId: data.block_id,
        title: data.title,
        datetime: data.datetime,
        entryPrice: data.entry_price,
        exitPrice: data.exit_price,
        strategyId: data.strategy_id,
        preTradeConfidence: data.pre_trade_confidence,
        postTradeConfidence: data.post_trade_confidence,
        sentiment: data.sentiment,
        screenshot: data.screenshot,
        notes: data.notes,
        status: data.status,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };

      console.log(`✅ Updated trade entry: ${entryId}`);
      return { success: true, entry: updatedEntry };

    } catch (error: any) {
      console.error('❌ Trade entry update exception:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete trade entry
   */
  static async deleteTradeEntry(entryId: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const { error } = await supabase
        .from('diary_trade_entries')
        .delete()
        .eq('id', entryId);

      if (error) {
        console.error('❌ Delete trade entry error:', error);
        return { success: false, error: error.message };
      }

      console.log(`✅ Deleted trade entry: ${entryId}`);
      return { success: true };

    } catch (error: any) {
      console.error('❌ Delete trade entry exception:', error);
      return { success: false, error: error.message };
    }
  }

  // =====================================================
  // SAVED REPORTS
  // =====================================================

  /**
   * Get all saved reports for current user
   */
  static async getUserReports(): Promise<SavedReport[]> {
    try {
      const { data, error } = await supabase
        .from('saved_reports')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching reports:', error);
        return [];
      }

      if (!data || data.length === 0) {
        return [];
      }

      const reports: SavedReport[] = data.map(report => ({
        id: report.id,
        userId: report.user_id,
        name: report.name,
        description: report.description,
        config: report.config,
        data: report.data,
        createdAt: report.created_at,
        updatedAt: report.updated_at,
        lastGeneratedAt: report.last_generated_at
      }));

      console.log(`✅ Loaded ${reports.length} saved reports`);
      return reports;

    } catch (error: any) {
      console.error('❌ Error loading reports:', error);
      return [];
    }
  }

  /**
   * Create new saved report
   */
  static async createReport(report: Partial<SavedReport>): Promise<{
    success: boolean;
    error?: string;
    report?: SavedReport;
  }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      let user = session?.user ?? null;
      let userError: any = null;
      if (!user) {
        const _u = await supabase.auth.getUser();
        user = _u.data.user;
        userError = _u.error;
      }

      if (userError || !user) {
        return { success: false, error: 'Not authenticated' };
      }

      const { data, error } = await supabase
        .from('saved_reports')
        .insert({
          user_id: user.id,
          name: report.name,
          description: report.description,
          config: report.config || {},
          data: report.data || {},
          last_generated_at: report.lastGeneratedAt
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Report creation error:', error);
        return { success: false, error: error.message };
      }

      const createdReport: SavedReport = {
        id: data.id,
        userId: data.user_id,
        name: data.name,
        description: data.description,
        config: data.config,
        data: data.data,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        lastGeneratedAt: data.last_generated_at
      };

      console.log(`✅ Created report: ${createdReport.name}`);
      return { success: true, report: createdReport };

    } catch (error: any) {
      console.error('❌ Report creation exception:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update saved report
   */
  static async updateReport(reportId: string, updates: Partial<SavedReport>): Promise<{
    success: boolean;
    error?: string;
    report?: SavedReport;
  }> {
    try {
      const updateData: any = {};

      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.config !== undefined) updateData.config = updates.config;
      if (updates.data !== undefined) updateData.data = updates.data;
      if (updates.lastGeneratedAt !== undefined) updateData.last_generated_at = updates.lastGeneratedAt;

      const { data, error } = await supabase
        .from('saved_reports')
        .update(updateData)
        .eq('id', reportId)
        .select()
        .single();

      if (error) {
        console.error('❌ Report update error:', error);
        return { success: false, error: error.message };
      }

      const updatedReport: SavedReport = {
        id: data.id,
        userId: data.user_id,
        name: data.name,
        description: data.description,
        config: data.config,
        data: data.data,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        lastGeneratedAt: data.last_generated_at
      };

      console.log(`✅ Updated report: ${reportId}`);
      return { success: true, report: updatedReport };

    } catch (error: any) {
      console.error('❌ Report update exception:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Delete saved report
   */
  static async deleteReport(reportId: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const { error } = await supabase
        .from('saved_reports')
        .delete()
        .eq('id', reportId);

      if (error) {
        console.error('❌ Delete report error:', error);
        return { success: false, error: error.message };
      }

      console.log(`✅ Deleted report: ${reportId}`);
      return { success: true };

    } catch (error: any) {
      console.error('❌ Delete report exception:', error);
      return { success: false, error: error.message };
    }
  }

  // =====================================================
  // HELPER FUNCTIONS
  // =====================================================

  /**
   * 📸 Upload image to Supabase Storage
   * Bucket: diary-images
   * Max size: 5MB per tester
   */
  static async uploadImage(
    base64Image: string,
    pageId: string,
    filename?: string
  ): Promise<{
    success: boolean;
    url?: string;
    error?: string;
  }> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      let user = session?.user ?? null;
      let userError: any = null;
      if (!user) {
        const _u = await supabase.auth.getUser();
        user = _u.data.user;
        userError = _u.error;
      }

      if (userError || !user) {
        return { success: false, error: 'Not authenticated' };
      }

      // Converti base64 in blob
      const base64Data = base64Image.split(',')[1];
      const mimeType = base64Image.match(/data:(.*?);/)?.[1] || 'image/jpeg';
      
      // Calcola dimensione (base64 è ~33% più grande del file originale)
      const sizeInBytes = (base64Data.length * 3) / 4;
      const sizeInMB = sizeInBytes / (1024 * 1024);
      
      // 🔒 LIMITE: 5MB per tester
      if (sizeInMB > 5) {
        return { 
          success: false, 
          error: `Image too large (${sizeInMB.toFixed(1)}MB). Max 5MB.` 
        };
      }

      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      // Genera nome file unico
      const timestamp = Date.now();
      const extension = mimeType.split('/')[1] || 'jpg';
      const finalFilename = filename || `img_${timestamp}.${extension}`;
      const filePath = `${user.id}/${pageId}/${finalFilename}`;

      console.log(`📤 Uploading image: ${filePath} (${sizeInMB.toFixed(2)}MB)`);

      // Upload a Supabase Storage
      const { data, error } = await supabase.storage
        .from('diary-images')
        .upload(filePath, blob, {
          contentType: mimeType,
          upsert: false
        });

      if (error) {
        console.error('❌ Upload error:', error);
        return { success: false, error: error.message };
      }

      // Ottieni URL pubblico
      const { data: publicUrlData } = supabase.storage
        .from('diary-images')
        .getPublicUrl(filePath);

      console.log(`✅ Image uploaded: ${publicUrlData.publicUrl}`);
      return { 
        success: true, 
        url: publicUrlData.publicUrl 
      };

    } catch (error: any) {
      console.error('❌ Upload exception:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * 🗑️ Delete image from Supabase Storage
   */
  static async deleteImage(imageUrl: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      // Estrai path dal URL
      const urlParts = imageUrl.split('/diary-images/');
      if (urlParts.length < 2) {
        return { success: false, error: 'Invalid image URL' };
      }

      const filePath = urlParts[1];

      const { error } = await supabase.storage
        .from('diary-images')
        .remove([filePath]);

      if (error) {
        console.error('❌ Delete image error:', error);
        return { success: false, error: error.message };
      }

      console.log(`✅ Image deleted: ${filePath}`);
      return { success: true };

    } catch (error: any) {
      console.error('❌ Delete image exception:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get diary statistics
   */
  static async getDiaryStats(): Promise<{
    totalPages: number;
    totalTradeEntries: number;
    pagesThisMonth: number;
    avgTradesPerPage: number;
  } | null> {
    try {
      const { data, error } = await supabase
        .rpc('get_diary_stats');

      if (error) {
        console.error('❌ Error fetching diary stats:', error);
        return null;
      }

      if (!data || data.length === 0) {
        return {
          totalPages: 0,
          totalTradeEntries: 0,
          pagesThisMonth: 0,
          avgTradesPerPage: 0
        };
      }

      const stats = data[0];
      return {
        totalPages: parseInt(stats.total_pages) || 0,
        totalTradeEntries: parseInt(stats.total_trade_entries) || 0,
        pagesThisMonth: parseInt(stats.pages_this_month) || 0,
        avgTradesPerPage: parseFloat(stats.avg_trades_per_page) || 0
      };

    } catch (error: any) {
      console.error('❌ Error loading diary stats:', error);
      return null;
    }
  }

  /**
   * Get recent diary activity
   */
  static async getRecentActivity(limit: number = 10): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .rpc('get_recent_diary_activity', { limit_count: limit });

      if (error) {
        console.error('❌ Error fetching recent activity:', error);
        return [];
      }

      return data || [];

    } catch (error: any) {
      console.error('❌ Error loading recent activity:', error);
      return [];
    }
  }
}

export default DiarySupabaseService;
